/**
 * Technology Icon Tooltips
 * Accessible tooltip system with mouse and touch support using modern Popover API
 */
class TechnologyTooltips {
	constructor() {
		this.tooltipGroups = new Map();
		this.delayTimer = null;
		this.currentTooltip = null;
		this.hasMouseEntered = false;
		this.INITIAL_DELAY = 500;
		this.SUBSEQUENT_DELAY = 0;
		this.supportsPopover = 'popover' in HTMLElement.prototype;

		this.init();
	}

	init() {
		// Find all tooltip groups
		const groups = document.querySelectorAll('[data-tooltip-group="tech-icons"]');

		groups.forEach((group) => {
			this.setupGroup(group);
		});
	}

	setupGroup(group) {
		const tooltipItems = group.querySelectorAll('.technology-item--with-icon[data-tooltip]');

		if (tooltipItems.length === 0) return;

		// Track this group
		const groupId = this.generateGroupId();
		this.tooltipGroups.set(groupId, {
			element: group,
			items: tooltipItems,
			hasRecentActivity: false,
			activityTimer: null,
		});

		// Setup each tooltip item
		tooltipItems.forEach((item, index) => {
			this.setupTooltipItem(item, groupId, index);
		});
	}

	setupTooltipItem(item, groupId, index) {
		// Create or setup the tooltip element
		let tooltip = item.querySelector('.tech-tooltip');
		if (!tooltip) {
			tooltip = this.createTooltipElement(item, groupId, index);
		} else {
			this.enhanceTooltipElement(tooltip, item, groupId, index);
		}

		// Mouse events
		item.addEventListener('mouseenter', (e) => this.handleMouseEnter(e, groupId));
		item.addEventListener('mouseleave', (e) => this.handleMouseLeave(e, groupId));

		// Touch events
		item.addEventListener('touchstart', (e) => this.handleTouchStart(e, groupId), {
			passive: true,
		});
		item.addEventListener('touchend', (e) => this.handleTouchEnd(e, groupId), { passive: true });

		// Keyboard events
		item.addEventListener('focus', (e) => this.handleFocus(e, groupId));
		item.addEventListener('blur', (e) => this.handleBlur(e, groupId));

		// Prevent default button behavior
		item.addEventListener('click', (e) => e.preventDefault());
	}

	createTooltipElement(item, groupId, index) {
		const tooltipText = item.getAttribute('data-tooltip');
		const tooltip = document.createElement('div');

		tooltip.className = 'tech-tooltip';
		tooltip.textContent = tooltipText;
		tooltip.setAttribute('role', 'tooltip');
		tooltip.setAttribute('aria-hidden', 'true');

		this.enhanceTooltipElement(tooltip, item, groupId, index);
		item.appendChild(tooltip);

		return tooltip;
	}

	enhanceTooltipElement(tooltip, item, groupId, index) {
		const tooltipId = `tech-tooltip-${groupId}-${index}`;

		if (this.supportsPopover) {
			// Use modern popover API
			tooltip.setAttribute('popover', 'manual');
			tooltip.id = tooltipId;
			item.setAttribute('popovertarget', tooltipId);

			// Move tooltip to body for proper top-layer positioning
			if (tooltip.parentNode !== document.body) {
				document.body.appendChild(tooltip);
			}
		} else {
			// Fallback: keep in DOM but use smart positioning
			tooltip.id = tooltipId;
		}

		item.setAttribute('aria-describedby', tooltipId);
	}

	handleMouseEnter(event, groupId) {
		const group = this.tooltipGroups.get(groupId);
		const item = event.currentTarget;

		// Clear any existing timer
		if (this.delayTimer) {
			clearTimeout(this.delayTimer);
		}

		// Hide current tooltip if different
		if (this.currentTooltip && this.currentTooltip !== item) {
			this.hideTooltip(this.currentTooltip);
		}

		// Determine delay based on recent activity in this group
		const delay = group.hasRecentActivity ? this.SUBSEQUENT_DELAY : this.INITIAL_DELAY;

		this.delayTimer = setTimeout(() => {
			this.showTooltip(item);
			this.markGroupActivity(groupId);
		}, delay);
	}

	handleMouseLeave(event, groupId) {
		const item = event.currentTarget;

		// Clear delay timer
		if (this.delayTimer) {
			clearTimeout(this.delayTimer);
			this.delayTimer = null;
		}

		// Hide tooltip
		this.hideTooltip(item);
	}

	handleTouchStart(event, groupId) {
		const item = event.currentTarget;

		// If tooltip is already visible, hide it
		if (item.classList.contains('tech-tooltip-visible')) {
			this.hideTooltip(item);
			return;
		}

		// Hide any other visible tooltips
		if (this.currentTooltip && this.currentTooltip !== item) {
			this.hideTooltip(this.currentTooltip);
		}

		// Show tooltip immediately on touch
		this.showTooltip(item);
		this.markGroupActivity(groupId);
	}

	handleTouchEnd(event, groupId) {
		// Touch end is handled by touchstart logic
		// This prevents double-triggering on touch devices
	}

	handleFocus(event, groupId) {
		const item = event.currentTarget;

		// Hide any other visible tooltips
		if (this.currentTooltip && this.currentTooltip !== item) {
			this.hideTooltip(this.currentTooltip);
		}

		// Show tooltip immediately on focus
		this.showTooltip(item);
		this.markGroupActivity(groupId);
	}

	handleBlur(event, groupId) {
		const item = event.currentTarget;
		this.hideTooltip(item);
	}

	showTooltip(item) {
		// Find tooltip by ID since it might have been moved to body for popover
		const tooltipId = item.getAttribute('aria-describedby');
		const tooltip = tooltipId
			? document.getElementById(tooltipId)
			: item.querySelector('.tech-tooltip');

		if (!tooltip) return;

		if (this.supportsPopover) {
			// Use popover API for modern browsers
			tooltip.showPopover();
			this.positionPopoverTooltip(tooltip, item);
		} else {
			// Fallback positioning for older browsers
			this.positionFallbackTooltip(tooltip, item);
		}

		// Update ARIA attributes
		tooltip.setAttribute('aria-hidden', 'false');

		// Add visible class
		item.classList.add('tech-tooltip-visible');

		// Track current tooltip
		this.currentTooltip = item;

		// Announce to screen readers
		this.announceTooltip(item);
	}

	hideTooltip(item) {
		if (!item) return;

		// Find tooltip by ID since it might have been moved to body for popover
		const tooltipId = item.getAttribute('aria-describedby');
		const tooltip = tooltipId
			? document.getElementById(tooltipId)
			: item.querySelector('.tech-tooltip');

		if (!tooltip) return;

		if (this.supportsPopover) {
			// Use popover API
			try {
				tooltip.hidePopover();
			} catch (e) {
				// Tooltip might already be hidden
			}
		}

		// Update ARIA attributes
		tooltip.setAttribute('aria-hidden', 'true');

		// Remove visible class
		item.classList.remove('tech-tooltip-visible');

		// Clear current tooltip if it's this one
		if (this.currentTooltip === item) {
			this.currentTooltip = null;
		}
	}

	positionPopoverTooltip(tooltip, item) {
		// Get item position
		const itemRect = item.getBoundingClientRect();

		// Position tooltip above the item, left-aligned with the icon
		const tooltipRect = tooltip.getBoundingClientRect();
		const left = itemRect.left;
		const top = itemRect.top - tooltipRect.height - 8;

		// Apply positioning with bounds checking
		tooltip.style.position = 'fixed';
		tooltip.style.left = `${Math.max(
			8,
			Math.min(left, window.innerWidth - tooltipRect.width - 8),
		)}px`;
		tooltip.style.top = `${Math.max(8, top)}px`;
		tooltip.style.zIndex = '2000';
	}

	positionFallbackTooltip(tooltip, item) {
		// For fallback, we'll use smart positioning within the scrollable container
		const container = item.closest('.member-map__list');
		if (!container) return;

		const containerRect = container.getBoundingClientRect();
		const itemRect = item.getBoundingClientRect();

		// Calculate relative position within container
		const relativeLeft = itemRect.left - containerRect.left;
		const relativeTop = itemRect.top - containerRect.top;

		// Set tooltip to visible first to measure it
		tooltip.style.visibility = 'hidden';
		tooltip.style.opacity = '1';

		const tooltipRect = tooltip.getBoundingClientRect();

		// Calculate optimal position
		let left = relativeLeft + itemRect.width / 2 - tooltipRect.width / 2;
		let top = relativeTop - tooltipRect.height - 8;

		// Adjust if tooltip would overflow container
		if (left < 8) {
			left = 8;
		} else if (left + tooltipRect.width > containerRect.width - 8) {
			left = containerRect.width - tooltipRect.width - 8;
		}

		// If tooltip would appear above container, show below instead
		if (top < 8) {
			top = relativeTop + itemRect.height + 8;
			tooltip.classList.add('tech-tooltip--below');
		} else {
			tooltip.classList.remove('tech-tooltip--below');
		}

		// Apply positioning
		tooltip.style.position = 'absolute';
		tooltip.style.left = `${left}px`;
		tooltip.style.top = `${top}px`;
		tooltip.style.visibility = 'visible';
	}

	markGroupActivity(groupId) {
		const group = this.tooltipGroups.get(groupId);
		if (!group) return;

		group.hasRecentActivity = true;

		// Clear existing timer
		if (group.activityTimer) {
			clearTimeout(group.activityTimer);
		}

		// Reset activity after 2 seconds of no interaction
		group.activityTimer = setTimeout(() => {
			group.hasRecentActivity = false;
		}, 2000);
	}

	announceTooltip(item) {
		const tooltipText = item.getAttribute('data-tooltip');
		if (!tooltipText) return;

		// Create a temporary element for screen reader announcement
		const announcement = document.createElement('div');
		announcement.setAttribute('aria-live', 'polite');
		announcement.setAttribute('aria-atomic', 'true');
		announcement.className = 'sr-only';
		announcement.textContent = tooltipText;

		document.body.appendChild(announcement);

		// Remove after announcement
		setTimeout(() => {
			document.body.removeChild(announcement);
		}, 1000);
	}

	generateGroupId() {
		return 'group_' + Math.random().toString(36).substr(2, 9);
	}

	// Cleanup method
	destroy() {
		// Clear all timers
		if (this.delayTimer) {
			clearTimeout(this.delayTimer);
		}

		this.tooltipGroups.forEach((group) => {
			if (group.activityTimer) {
				clearTimeout(group.activityTimer);
			}
		});

		// Clear references
		this.tooltipGroups.clear();
		this.currentTooltip = null;
	}
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
	new TechnologyTooltips();
});

// Handle dynamic content (if tooltips are added via AJAX)
window.TechnologyTooltips = TechnologyTooltips;
